/**
 * <music-video-grid> — searchable, sortable, paginated grid of Bunny Stream
 * videos, rendered from the /api/videos proxy. Vanilla custom element; no
 * framework and no build step.
 *
 * All configuration arrives as data attributes from the section Liquid.
 */
(() => {
  if (customElements.get('music-video-grid')) return;

  const SORTS = ['latest', 'oldest', 'a-z', 'z-a'];
  const SEARCH_DEBOUNCE_MS = 400;
  const CACHE_TTL_MS = 60000;

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (character) => {
      switch (character) {
        case '&':
          return '&amp;';
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '"':
          return '&quot;';
        default:
          return '&#39;';
      }
    });

  const formatDuration = (seconds) => {
    if (!Number.isFinite(seconds) || seconds <= 0) return '';

    const total = Math.round(seconds);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const remainder = total % 60;
    const pad = (value) => String(value).padStart(2, '0');

    return hours > 0 ? `${hours}:${pad(minutes)}:${pad(remainder)}` : `${minutes}:${pad(remainder)}`;
  };

  class MusicVideoGrid extends HTMLElement {
    #state = { page: 1, sort: 'latest', search: '', totalPages: 0, totalItems: 0, status: 'idle' };
    #cache = new Map();
    #controller = null;
    #searchTimer = null;
    #lastTrigger = null;

    connectedCallback() {
      this.endpoint = this.dataset.endpoint || '';
      this.perPage = Number.parseInt(this.dataset.perPage, 10) || 24;
      this.thumbnailWidth = Number.parseInt(this.dataset.thumbnailWidth, 10) || 480;
      this.collection = this.dataset.collection || '';
      this.showDate = this.dataset.showDate === 'true';
      this.showDuration = this.dataset.showDuration === 'true';
      this.syncUrl = this.dataset.syncUrl === 'true';

      this.grid = this.querySelector('[data-grid]');
      this.status = this.querySelector('[data-status]');
      this.pagination = this.querySelector('[data-pagination]');
      this.pageLabel = this.querySelector('[data-page-label]');
      this.prevButton = this.querySelector('[data-prev]');
      this.nextButton = this.querySelector('[data-next]');
      this.searchInput = this.querySelector('[data-search]');
      this.searchClear = this.querySelector('[data-search-clear]');
      this.sortSelect = this.querySelector('[data-sort-select]');
      this.dialog = this.querySelector('[data-dialog]');
      this.player = this.querySelector('[data-player]');
      this.dialogTitle = this.querySelector('[data-dialog-title]');

      this.#state.sort = SORTS.includes(this.dataset.sort) ? this.dataset.sort : 'latest';
      this.#readUrlState();

      if (this.searchInput) this.searchInput.value = this.#state.search;
      if (this.sortSelect) this.sortSelect.value = this.#state.sort;
      this.#toggleSearchClear();

      this.#bindEvents();
      this.#load();
    }

    disconnectedCallback() {
      this.#controller?.abort();
      clearTimeout(this.#searchTimer);
    }

    /* -------------------------------------------------------------------- *
     * Events
     * -------------------------------------------------------------------- */

    #bindEvents() {
      this.searchInput?.addEventListener('input', () => {
        clearTimeout(this.#searchTimer);
        this.#toggleSearchClear();
        this.#searchTimer = setTimeout(() => {
          this.#state.search = this.searchInput.value.trim();
          this.#state.page = 1;
          this.#load();
        }, SEARCH_DEBOUNCE_MS);
      });

      // A form wraps the input so Enter submits instead of reloading the page.
      this.querySelector('[data-search-form]')?.addEventListener('submit', (event) => {
        event.preventDefault();
        clearTimeout(this.#searchTimer);
        this.#state.search = this.searchInput.value.trim();
        this.#state.page = 1;
        this.#load();
      });

      this.searchClear?.addEventListener('click', () => {
        clearTimeout(this.#searchTimer);
        this.searchInput.value = '';
        this.searchInput.focus();
        this.#toggleSearchClear();
        if (!this.#state.search) return;
        this.#state.search = '';
        this.#state.page = 1;
        this.#load();
      });

      this.sortSelect?.addEventListener('change', () => {
        this.#state.sort = SORTS.includes(this.sortSelect.value) ? this.sortSelect.value : 'latest';
        this.#state.page = 1;
        this.#load();
      });

      this.prevButton?.addEventListener('click', () => this.#goToPage(this.#state.page - 1));
      this.nextButton?.addEventListener('click', () => this.#goToPage(this.#state.page + 1));

      // A thumbnail that 404s would otherwise render as a broken-image icon;
      // hiding it falls back to the neutral media box. `error` does not bubble,
      // so this has to listen during the capture phase.
      this.grid?.addEventListener(
        'error',
        (event) => {
          const image = event.target;
          if (image instanceof HTMLImageElement) image.classList.add('is-broken');
        },
        true
      );

      this.grid?.addEventListener('click', (event) => {
        // Each card is a real link to the Bunny play page, so leave the
        // browser's own handling alone for middle clicks and modifier clicks.
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        const card = event.target instanceof Element ? event.target.closest('[data-video-id]') : null;
        if (!card) return;

        event.preventDefault();
        this.#openPlayer(card);
      });

      // `close` covers the paths the component does not drive itself, chiefly
      // the Escape key. #teardownPlayer is idempotent, so the double call from
      // an explicit close is harmless.
      this.dialog?.addEventListener('close', () => this.#teardownPlayer());
      this.querySelector('[data-dialog-close]')?.addEventListener('click', () => this.#closePlayer());

      // Clicking the backdrop (the dialog element itself) dismisses the player.
      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.#closePlayer();
      });
    }

    #goToPage(page) {
      const target = Math.min(Math.max(page, 1), this.#state.totalPages || 1);
      if (target === this.#state.page) return;

      this.#state.page = target;
      this.#load({ scroll: true });
    }

    /* -------------------------------------------------------------------- *
     * Data
     * -------------------------------------------------------------------- */

    #buildUrl() {
      const url = new URL(this.endpoint, window.location.origin);
      url.searchParams.set('page', String(this.#state.page));
      url.searchParams.set('itemsPerPage', String(this.perPage));
      url.searchParams.set('sort', this.#state.sort);
      if (this.#state.search) url.searchParams.set('search', this.#state.search);
      if (this.collection) url.searchParams.set('collection', this.collection);
      return url;
    }

    async #load({ scroll = false } = {}) {
      if (!this.endpoint) {
        this.#renderError(this.dataset.errorText);
        return;
      }

      const url = this.#buildUrl();
      const key = url.toString();

      this.#controller?.abort();
      this.#writeUrlState();

      const cached = this.#cache.get(key);
      if (cached && cached.expiresAt > Date.now()) {
        this.#renderResults(cached.payload, { scroll });
        return;
      }

      this.#renderSkeletons();

      const controller = new AbortController();
      this.#controller = controller;

      try {
        const response = await fetch(key, {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

        const payload = this.#normalize(await response.json());

        this.#cache.set(key, { payload, expiresAt: Date.now() + CACHE_TTL_MS });
        this.#renderResults(payload, { scroll });
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error('[music-video-grid]', error);
        this.#renderError(this.dataset.errorText);
      } finally {
        if (this.#controller === controller) this.#controller = null;
      }
    }

    /**
     * Accepts either the proxy's shape or a raw Bunny Stream page, so the
     * section keeps working against an older proxy deployment.
     */
    #normalize(payload) {
      const libraryId = payload?.libraryId || '';
      const pullZone = payload?.pullZone || '';
      const perPage = Number(payload?.itemsPerPage) || this.perPage;
      const totalItems = Number(payload?.totalItems) || 0;

      const items = (Array.isArray(payload?.items) ? payload.items : []).map((item) => {
        const id = item?.id || item?.guid || '';
        const thumbnailFileName = item?.thumbnailFileName || 'thumbnail.jpg';
        const cdnRoot = pullZone && id ? `https://${pullZone}/${id}` : '';

        return {
          id,
          title: item?.title ?? '',
          date: item?.date ?? item?.dateUploaded ?? null,
          duration: Number(item?.duration ?? item?.length) || 0,
          thumbnail: item?.thumbnail || (cdnRoot ? `${cdnRoot}/${thumbnailFileName}` : ''),
          embedUrl:
            item?.embedUrl ||
            (libraryId && id ? `https://iframe.mediadelivery.net/embed/${libraryId}/${id}` : ''),
          playUrl:
            item?.playUrl || (libraryId && id ? `https://video.bunnycdn.com/play/${libraryId}/${id}` : ''),
        };
      });

      return {
        items,
        totalItems,
        totalPages: Number(payload?.totalPages) || Math.ceil(totalItems / perPage) || 0,
      };
    }

    /* -------------------------------------------------------------------- *
     * Rendering
     * -------------------------------------------------------------------- */

    #renderSkeletons() {
      this.#state.status = 'loading';
      this.setAttribute('data-loading', 'true');

      const card =
        '<div class="music-video-card music-video-card--skeleton" aria-hidden="true">' +
        '<div class="music-video-card__media music-video-skeleton"></div>' +
        '<div class="music-video-card__body">' +
        '<span class="music-video-skeleton music-video-skeleton--line"></span>' +
        '<span class="music-video-skeleton music-video-skeleton--line music-video-skeleton--short"></span>' +
        '</div></div>';

      this.grid.innerHTML = card.repeat(this.perPage);
      this.#setStatus(this.dataset.loadingText, 'loading');
      this.#renderPagination();
    }

    #renderError(message) {
      this.#state.status = 'error';
      this.removeAttribute('data-loading');
      this.grid.innerHTML = '';
      this.#setStatus(message || 'Something went wrong.', 'error');
      this.#renderPagination();
    }

    #renderResults(payload, { scroll = false } = {}) {
      this.#state.status = 'ready';
      this.#state.totalItems = payload.totalItems;
      this.#state.totalPages = payload.totalPages;
      this.removeAttribute('data-loading');

      if (!payload.items.length) {
        this.grid.innerHTML = '';
        this.#setStatus(this.dataset.emptyText, 'empty');
        this.#renderPagination();
        return;
      }

      this.grid.innerHTML = payload.items.map((video) => this.#cardMarkup(video)).join('');
      this.#setStatus(this.#resultsSummary(), 'results');
      this.#renderPagination();

      if (scroll) this.#scrollIntoView();
    }

    #cardMarkup(video) {
      const title = escapeHtml(video.title);
      const duration = this.showDuration ? formatDuration(video.duration) : '';
      const date = this.showDate ? this.#formatDate(video.date) : '';
      const thumbnail = video.thumbnail
        ? `${video.thumbnail}${video.thumbnail.includes('?') ? '&' : '?'}width=${this.thumbnailWidth}`
        : '';

      const media = thumbnail
        ? `<img class="music-video-card__image" src="${escapeHtml(thumbnail)}" alt="" loading="lazy" decoding="async" width="480" height="270">`
        : '<span class="music-video-card__image music-video-card__image--empty"></span>';

      const meta = [duration, date]
        .filter(Boolean)
        .map((value) => `<span class="music-video-card__meta-item">${escapeHtml(value)}</span>`)
        .join('');

      return `
        <article class="music-video-card">
          <a class="music-video-card__link"
             href="${escapeHtml(video.playUrl)}"
             data-video-id="${escapeHtml(video.id)}"
             data-video-embed="${escapeHtml(video.embedUrl)}"
             data-video-title="${title}">
            <span class="music-video-card__media">
              ${media}
              <span class="music-video-card__play" aria-hidden="true">
                <svg viewBox="0 0 48 48" fill="none" focusable="false">
                  <circle cx="24" cy="24" r="23" stroke="currentColor" stroke-width="2"></circle>
                  <path d="M19 15.5 34 24l-15 8.5v-17Z" fill="currentColor"></path>
                </svg>
              </span>
            </span>
            <span class="music-video-card__body">
              <span class="music-video-card__title h6">${title}</span>
              ${meta ? `<span class="music-video-card__meta text-sm text-subdued">${meta}</span>` : ''}
            </span>
          </a>
        </article>
      `;
    }

    #renderPagination() {
      if (!this.pagination) return;

      const { page, totalPages, status } = this.#state;
      const loading = status === 'loading';

      this.prevButton.disabled = loading || page <= 1;
      this.nextButton.disabled = loading || page >= totalPages;

      this.pageLabel.textContent = loading
        ? this.dataset.loadingText
        : this.#interpolate(this.dataset.pageText, { page, total: totalPages });

      // A single page of results needs no controls, but keep them during the
      // first load so the section does not jump once the count arrives.
      this.pagination.hidden = totalPages <= 1 && !loading;
    }

    #setStatus(message, kind) {
      if (!this.status) return;
      this.status.textContent = message || '';
      this.status.dataset.kind = kind;
      this.status.hidden = !message || (kind === 'results' && this.dataset.showCount !== 'true');
    }

    #resultsSummary() {
      const key = this.#state.search ? 'searchCountText' : 'countText';
      return this.#interpolate(this.dataset[key], {
        count: this.#state.totalItems,
        query: this.#state.search,
      });
    }

    /**
     * Fills [page], [total], [count] and [query] in the merchant's text.
     *
     * Deliberately not Liquid's {{ }}: Shopify reads that inside a text
     * setting's value as a dynamic source binding and rejects the setting.
     */
    #interpolate(template, values) {
      return String(template || '').replace(/\[(\w+)\]/g, (match, token) =>
        Object.prototype.hasOwnProperty.call(values, token) ? String(values[token]) : match
      );
    }

    /** "Mar 15 2024" — matches the format the deployed React card used (moment's "MMM DD YYYY"). */
    #formatDate(value) {
      if (!value) return '';

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';

      try {
        const formatted = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
        }).format(date);

        // Locale formats typically separate the parts with a comma; drop it
        // for parity with the original "MMM DD YYYY" (no punctuation).
        return formatted.replace(/,/g, '');
      } catch {
        return date.toISOString().slice(0, 10);
      }
    }

    #toggleSearchClear() {
      if (this.searchClear) this.searchClear.hidden = !this.searchInput?.value;
    }

    #scrollIntoView() {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }

    /* -------------------------------------------------------------------- *
     * Player
     * -------------------------------------------------------------------- */

    #openPlayer(trigger) {
      const embedUrl = trigger.dataset.videoEmbed;
      if (!embedUrl || !this.dialog) return;

      this.#lastTrigger = trigger;

      const iframe = document.createElement('iframe');
      iframe.src = `${embedUrl}${embedUrl.includes('?') ? '&' : '?'}autoplay=true`;
      iframe.loading = 'lazy';
      iframe.title = trigger.dataset.videoTitle || '';
      iframe.allow = 'accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;

      this.player.replaceChildren(iframe);
      if (this.dialogTitle) this.dialogTitle.textContent = trigger.dataset.videoTitle || '';

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      document.documentElement.classList.add('music-video-grid-open');
    }

    #closePlayer() {
      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }

      this.#teardownPlayer();
    }

    /**
     * Removing the iframe is what actually stops playback, so this must run on
     * every dismissal path. Safe to call twice.
     */
    #teardownPlayer() {
      if (!this.player.firstChild && !this.#lastTrigger) return;

      this.player.replaceChildren();
      document.documentElement.classList.remove('music-video-grid-open');
      this.#lastTrigger?.focus();
      this.#lastTrigger = null;
    }

    /* -------------------------------------------------------------------- *
     * URL state
     * -------------------------------------------------------------------- */

    #paramName(key) {
      return `${this.dataset.urlPrefix || 'mv'}_${key}`;
    }

    #readUrlState() {
      if (!this.syncUrl) return;

      const params = new URLSearchParams(window.location.search);
      const page = Number.parseInt(params.get(this.#paramName('page')), 10);
      const sort = params.get(this.#paramName('sort'));
      const search = params.get(this.#paramName('q'));

      if (Number.isFinite(page) && page > 0) this.#state.page = page;
      if (SORTS.includes(sort)) this.#state.sort = sort;
      if (search) this.#state.search = search.trim();
    }

    #writeUrlState() {
      if (!this.syncUrl) return;

      const url = new URL(window.location.href);
      const set = (key, value, fallback) => {
        if (value === fallback) url.searchParams.delete(this.#paramName(key));
        else url.searchParams.set(this.#paramName(key), String(value));
      };

      set('page', this.#state.page, 1);
      set('sort', this.#state.sort, this.dataset.sort || 'latest');
      set('q', this.#state.search, '');

      window.history.replaceState(window.history.state, '', url);
    }
  }

  customElements.define('music-video-grid', MusicVideoGrid);
})();
